import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  OBJECTIVE_RELATION_LABELS,
  OBJECTIVE_RELATION_TYPES,
  OBJECTIVE_STATUS_LABELS,
  objectiveListSchema,
  objectiveOptionsSchema,
  objectiveSchema,
  objectiveWeightInputSchema,
  objectiveWeightTotal,
  KPI_DIRECTION_LABELS,
  KPI_PERIODICITY_LABELS,
  kpiListSchema,
  kpiUnitLabel,
  perspectiveListSchema,
  type Objective,
  type ObjectiveInput,
  type ObjectiveOptions,
  type ObjectiveRelationTypeName,
  type ObjectiveStatusName,
  type Perspective,
  type TrafficLight,
} from '@fluxus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderKanban, Gauge, GripVertical, Link2, Lock, Pencil, Plus } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { cn } from '@/lib/cn';
import { ObjectiveForm } from '@/pages/ObjetivosPage';

const SERIES = ['bg-series-1', 'bg-series-2', 'bg-series-3', 'bg-series-4', 'bg-series-5', 'bg-series-6'];
const formatWeight = (w: number) => `${w.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;
const STATUS_LIGHT: Record<ObjectiveStatusName, TrafficLight> = {
  PLANNED: 'gray',
  IN_PROGRESS: 'blue',
  AT_RISK: 'yellow',
  DONE: 'green',
  SUSPENDED: 'gray',
};

/** Estratégia › Construção do BSC (T-043, RF-16): o BSC montado em colunas por perspectiva. */
export function ConstrucaoBscPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);

  return (
    <PageContainer className="max-w-[90rem]">
      <PageHeader
        title="Construção do BSC"
        description="Monte o BSC da empresa: os objetivos de cada perspectiva, na ordem de importância, com os pesos e as conexões entre eles."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="O BSC pertence a um projeto. Quando a empresa tiver um projeto em andamento, ele aparece aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <div key={project.id} className="mt-4 flex flex-col gap-4">
          <ProjectPicker projects={projects} project={project} onSelect={select} />
          {project.status !== 'ACTIVE' ? (
            <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
              <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              Projeto arquivado: o BSC fica disponível só para consulta.
            </p>
          ) : null}
          <Board
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </div>
      )}
    </PageContainer>
  );
}

function Board({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const user = useCurrentUser();
  const canDo = useCan();
  const can = (action: 'create' | 'edit') => !readOnly && canDo('objetivos', action);
  const queryClient = useQueryClient();
  const project = `/organizations/${organizationId}/projects/${projectId}`;
  const base = `${project}/objectives`;
  const boardKey = ['objectives', projectId, 'board'];
  const [editing, setEditing] = useState<{ objective?: Objective; perspectiveId?: string } | null>(null);
  const [relating, setRelating] = useState<Objective | null>(null);
  // PRD §11.7 "acessar indicadores vinculados" (DEC-025).
  const [kpisOf, setKpisOf] = useState<Objective | null>(null);
  const canViewKpis = canDo('indicadores', 'view');
  const [busy, setBusy] = useState(false);

  const perspectives = useQuery({
    queryKey: ['perspectives', projectId, false],
    queryFn: ({ signal }) =>
      client.request(`${project}/perspectives`, { schema: perspectiveListSchema, signal }),
  });
  const objectives = useQuery({
    queryKey: boardKey,
    queryFn: async ({ signal }) => {
      const all: Objective[] = [];
      for (let page = 1, pages = 1; page <= pages; page++) {
        const res = await client.request(base, {
          query: { page: String(page), pageSize: '100' },
          schema: objectiveListSchema,
          signal,
        });
        all.push(...res.items);
        pages = res.totalPages;
      }
      return all;
    },
  });
  const options = useQuery({
    queryKey: ['objectives', projectId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: objectiveOptionsSchema, signal }),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['objectives', projectId] });

  const reorder = useMutation({
    meta: { handlesError: true },
    mutationFn: (body: { perspectiveId: string; ids: string[] }) =>
      client.request(`${base}/order`, { method: 'PUT', body, schema: objectiveListSchema }),
    onSuccess: () => refresh(),
    onError: async (err) => {
      toast.error('Não foi possível reordenar', { description: errorMessage(err) });
      await refresh();
    },
  });

  const sensors = useSensors(
    // Sensores separados por tipo: o mouse começa a arrastar ao mover 6 px; no toque é preciso segurar
    // um instante (180 ms) — um deslize rápido no puxador não vira arraste por engano.
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const all = objectives.data ?? [];
  const nameOf = (id: unknown) => all.find((o) => o.id === id)?.name ?? 'o objetivo';
  const announcements: Announcements = {
    onDragStart: ({ active }) => `Movendo ${nameOf(active.id)}.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${nameOf(active.id)} sobre a posição de ${nameOf(over.id)}.`
        : `${nameOf(active.id)} fora da lista.`,
    onDragEnd: ({ active, over }) =>
      over ? `${nameOf(active.id)} colocado na posição de ${nameOf(over.id)}.` : 'Movimento cancelado.',
    onDragCancel: ({ active }) => `Movimento de ${nameOf(active.id)} cancelado.`,
  };

  // Ordenar vale dentro da perspectiva: durante o arraste, só os cartões da mesma coluna reagem.
  const sameColumn: CollisionDetection = (args) =>
    closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter(
        (c) => c.data.current?.sortable?.containerId === args.active.data.current?.sortable?.containerId,
      ),
    });

  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const moved = all.find((o) => o.id === active.id);
    const target = all.find((o) => o.id === over.id);
    // Ordenar é dentro da perspectiva: soltar em outra coluna não muda nada.
    if (!moved || !target || moved.perspective.id !== target.perspective.id) return;
    const column = all.filter((o) => o.perspective.id === moved.perspective.id);
    const ids = column.map((o) => o.id);
    const from = ids.indexOf(moved.id);
    const to = ids.indexOf(target.id);
    ids.splice(from, 1);
    ids.splice(to, 0, moved.id);
    // Mostra a nova ordem na hora; a API confirma (ou o quadro volta ao que está salvo).
    queryClient.setQueryData<Objective[]>(boardKey, (prev) => {
      if (!prev) return prev;
      const rest = prev.filter((o) => o.perspective.id !== moved.perspective.id);
      return [...rest, ...ids.map((id) => prev.find((o) => o.id === id)!)];
    });
    reorder.mutate({ perspectiveId: moved.perspective.id, ids });
  }

  async function save(input: ObjectiveInput) {
    const target = editing?.objective;
    const saved = await client.request(target ? `${base}/${target.id}` : base, {
      method: target ? 'PUT' : 'POST',
      body: input,
      schema: objectiveSchema,
    });
    setEditing(null);
    toast.success(target ? 'Objetivo salvo' : 'Objetivo criado', { description: saved.name });
    await refresh();
  }

  if (perspectives.isPending || objectives.isPending) {
    return <LoadingState rows={4} label="Carregando o BSC" />;
  }
  if (perspectives.isError || objectives.isError) {
    return (
      <ErrorState
        error={perspectives.error ?? objectives.error}
        onRetry={() => {
          void perspectives.refetch();
          void objectives.refetch();
        }}
        retrying={perspectives.isFetching || objectives.isFetching}
      />
    );
  }

  const columns = perspectives.data.items;
  return (
    <>
      <p className="text-sm text-muted-foreground">
        {can('edit')
          ? 'Arraste pelo puxador (ou use Espaço e as setas) para ordenar dentro da perspectiva. O peso é salvo ao sair do campo.'
          : 'Visão do BSC por perspectiva.'}
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={sameColumn}
        onDragEnd={onDragEnd}
        accessibility={{
          announcements,
          screenReaderInstructions: {
            draggable:
              'Para mover, pressione Espaço; use as setas para cima e para baixo e Espaço de novo para soltar. Esc cancela.',
          },
        }}
      >
        <div
          role="region"
          aria-label="Quadro do BSC"
          tabIndex={0}
          className="flex flex-col gap-4 lg:grid lg:auto-cols-[minmax(17rem,1fr)] lg:grid-flow-col lg:overflow-x-auto lg:pb-2"
        >
          {columns.map((p, i) => (
            <Column
              key={p.id}
              perspective={p}
              color={SERIES[i % SERIES.length]!}
              objectives={all.filter((o) => o.perspective.id === p.id)}
              canCreate={can('create')}
              canEdit={can('edit')}
              partialView={user.areaScope !== 'ALL'}
              dragging={reorder.isPending}
              organizationId={organizationId}
              projectId={projectId}
              onAdd={() => setEditing({ perspectiveId: p.id })}
              onEdit={(o) => setEditing({ objective: o })}
              onRelate={(o) => setRelating(o)}
              onKpis={canViewKpis ? (o) => setKpisOf(o) : undefined}
            />
          ))}
        </div>
      </DndContext>

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        busy={busy}
        title={editing?.objective ? 'Editar objetivo' : 'Novo objetivo'}
        className="w-[min(42rem,calc(100vw-2rem))]"
      >
        {editing !== null && options.data ? (
          <ObjectiveForm
            initial={editing.objective}
            options={options.data}
            defaultPerspectiveId={editing.perspectiveId}
            onSubmit={save}
            onCancel={() => setEditing(null)}
            onBusyChange={setBusy}
          />
        ) : editing !== null ? (
          options.isError ? (
            <ErrorState
              error={options.error}
              onRetry={() => void options.refetch()}
              retrying={options.isFetching}
            />
          ) : (
            <LoadingState rows={3} label="Carregando o formulário" />
          )
        ) : null}
      </Dialog>
      <Dialog
        open={relating !== null}
        onOpenChange={(open) => !open && setRelating(null)}
        busy={busy}
        title="Relacionar objetivo"
      >
        {relating && options.data ? (
          <RelateForm
            objective={relating}
            options={options.data}
            onCancel={() => setRelating(null)}
            onBusyChange={setBusy}
            onSubmit={async (type, objectiveId) => {
              // Só acrescenta a relação: o resto do objetivo fica como está no servidor.
              const saved = await client.request(`${base}/${relating.id}/relations`, {
                method: 'POST',
                body: { objectiveId, type },
                schema: objectiveSchema,
              });
              setRelating(null);
              toast.success('Relação criada', { description: saved.name });
              await refresh();
            }}
          />
        ) : relating ? (
          options.isError ? (
            <ErrorState
              error={options.error}
              onRetry={() => void options.refetch()}
              retrying={options.isFetching}
            />
          ) : (
            <LoadingState rows={2} label="Carregando os objetivos" />
          )
        ) : null}
      </Dialog>
      <Dialog
        open={kpisOf !== null}
        onOpenChange={(open) => !open && setKpisOf(null)}
        title="Indicadores vinculados"
        className="w-[min(36rem,calc(100vw-2rem))]"
      >
        {kpisOf ? (
          <LinkedKpis organizationId={organizationId} projectId={projectId} objective={kpisOf} />
        ) : null}
      </Dialog>
    </>
  );
}

/** KPIs que medem o objetivo, com atalho para a tela de indicadores já filtrada. */
function LinkedKpis({
  organizationId,
  projectId,
  objective,
}: {
  organizationId: string;
  projectId: string;
  objective: Objective;
}) {
  const { client } = useAuth();
  const kpis = useQuery({
    queryKey: ['kpis', projectId, 'objective', objective.id],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/projects/${projectId}/kpis`, {
        query: { objectiveId: objective.id, pageSize: '100' },
        schema: kpiListSchema,
        signal,
      }),
  });
  const kpisPage = `/empresas/${organizationId}/indicadores/kpis?objetivo=${objective.id}`;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        <span className="font-semibold text-foreground">{objective.name}</span>
      </p>
      {kpis.isPending ? (
        <LoadingState rows={2} label="Carregando os indicadores" />
      ) : kpis.isError ? (
        <ErrorState error={kpis.error} onRetry={() => void kpis.refetch()} retrying={kpis.isFetching} />
      ) : kpis.data.items.length === 0 ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-8"
          icon={Gauge}
          title="Nenhum indicador mede este objetivo"
          description="Cadastre um indicador e ligue-o a este objetivo na tela de indicadores."
        />
      ) : (
        <ul aria-label={`Indicadores de ${objective.name}`} className="flex flex-col gap-2">
          {kpis.data.items.map((k) => {
            const link = k.objectives.find((o) => o.id === objective.id);
            return (
              <li key={k.id} className="rounded-md border border-border bg-surface p-3">
                <p className="font-semibold">
                  <span className="mr-2 font-mono text-xs text-muted-foreground">{k.code}</span>
                  {k.name}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {kpiUnitLabel(k)} · {KPI_PERIODICITY_LABELS[k.periodicity]} ·{' '}
                  {KPI_DIRECTION_LABELS[k.direction]}
                  {link?.weight !== null && link?.weight !== undefined
                    ? ` · peso ${formatWeight(link.weight)}`
                    : ''}
                </p>
              </li>
            );
          })}
        </ul>
      )}
      {kpis.data && kpis.data.total > kpis.data.items.length ? (
        <p className="text-sm text-muted-foreground">
          Mostrando {kpis.data.items.length} de {kpis.data.total} indicadores. Veja todos em Indicadores.
        </p>
      ) : null}
      <div className="flex justify-end">
        <Button asChild variant="outline">
          <Link to={kpisPage}>Abrir em Indicadores</Link>
        </Button>
      </div>
    </div>
  );
}

function Column({
  perspective,
  color,
  objectives,
  canCreate,
  canEdit,
  partialView,
  dragging,
  organizationId,
  projectId,
  onAdd,
  onEdit,
  onRelate,
  onKpis,
}: {
  perspective: Perspective;
  color: string;
  objectives: Objective[];
  canCreate: boolean;
  canEdit: boolean;
  /** A pessoa vê só os objetivos das suas áreas: a soma é dos que ela vê. */
  partialView: boolean;
  dragging: boolean;
  organizationId: string;
  projectId: string;
  onAdd: () => void;
  onEdit: (o: Objective) => void;
  onRelate: (o: Objective) => void;
  /** Ausente quando a pessoa não vê o módulo de indicadores. */
  onKpis?: (o: Objective) => void;
}) {
  const total = objectiveWeightTotal(objectives);
  const weighted = objectives.some((o) => o.weight !== null);
  return (
    <section
      aria-label={perspective.name}
      className="flex min-w-0 flex-col rounded-lg border border-border bg-muted/40"
    >
      <header className="flex flex-col gap-1 border-b border-border p-3">
        <div className="flex items-center gap-2">
          <span className={cn('size-2.5 shrink-0 rounded-full', color)} aria-hidden />
          <h2 className="min-w-0 flex-1 truncate font-bold" title={perspective.name}>
            {perspective.name}
          </h2>
          <span className="text-xs text-muted-foreground">
            {perspective.weight === null ? 'sem peso' : formatWeight(perspective.weight)}
          </span>
        </div>
        <p
          className={cn(
            'text-xs',
            !weighted || partialView
              ? 'text-muted-foreground'
              : total === 100
                ? 'text-status-green-fg'
                : 'text-status-yellow-fg',
          )}
        >
          {!weighted
            ? 'Objetivos sem peso definido'
            : partialView
              ? `Pesos dos objetivos que você vê: ${formatWeight(total)}`
              : total === 100
                ? 'Pesos dos objetivos: 100%'
                : `Pesos dos objetivos somam ${formatWeight(total)} — o ideal é 100%`}
        </p>
      </header>
      <SortableContext
        id={perspective.id}
        items={objectives.map((o) => o.id)}
        strategy={verticalListSortingStrategy}
      >
        <ol
          aria-label={`Objetivos de ${perspective.name}`}
          aria-busy={dragging}
          className="flex flex-col gap-2 p-3"
        >
          {objectives.map((o) => (
            <Card
              key={o.id}
              objective={o}
              canEdit={canEdit}
              organizationId={organizationId}
              projectId={projectId}
              onEdit={() => onEdit(o)}
              onRelate={() => onRelate(o)}
              onKpis={onKpis ? () => onKpis(o) : undefined}
            />
          ))}
          {objectives.length === 0 ? (
            <li className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
              Nenhum objetivo nesta perspectiva.
            </li>
          ) : null}
        </ol>
      </SortableContext>
      {canCreate ? (
        <div className="mt-auto p-3 pt-0">
          {/* O nome da perspectiva pode ser longo: o texto quebra em vez de passar da borda no celular. */}
          <Button
            variant="outline"
            size="sm"
            className="h-auto min-h-8 w-full py-1.5 text-center whitespace-normal"
            onClick={onAdd}
          >
            <Plus aria-hidden />
            Adicionar objetivo em {perspective.name}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

function Card({
  objective: o,
  canEdit,
  organizationId,
  projectId,
  onEdit,
  onRelate,
  onKpis,
}: {
  objective: Objective;
  canEdit: boolean;
  organizationId: string;
  projectId: string;
  onEdit: () => void;
  onRelate: () => void;
  onKpis?: () => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({
      id: o.id,
      disabled: !canEdit,
    });
  const relationCount = o.relations.length + o.relatedFrom.length;
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex gap-2 rounded-md border border-border bg-surface p-3 shadow-sm',
        isDragging && 'relative z-10 opacity-80 shadow-md ring-2 ring-brand/40',
      )}
    >
      {canEdit ? (
        <button
          ref={setActivatorNodeRef}
          type="button"
          aria-label={`Arrastar ${o.name}`}
          className="-ml-1 flex w-7 shrink-0 cursor-grab touch-none items-start justify-center rounded pt-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="font-semibold leading-snug">{o.name}</p>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={STATUS_LIGHT[o.status]} label={OBJECTIVE_STATUS_LABELS[o.status]} />
          {relationCount ? (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Link2 className="size-3.5" aria-hidden />
              {relationCount} {relationCount === 1 ? 'relação' : 'relações'}
            </span>
          ) : null}
        </div>
        {o.relations.length ? (
          <ul className="flex flex-col gap-0.5 text-xs text-muted-foreground">
            {o.relations.map((r) => (
              <li key={`${r.type}:${r.objective.id}`}>
                {OBJECTIVE_RELATION_LABELS[r.type]} → {r.objective.name}
                {r.objective.archived ? ' (arquivado)' : ''}
              </li>
            ))}
          </ul>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-2">
          {canEdit ? (
            <WeightField
              key={String(o.weight)}
              objective={o}
              organizationId={organizationId}
              projectId={projectId}
            />
          ) : (
            <span className="text-xs text-muted-foreground">
              {o.weight === null ? 'Sem peso' : `Peso ${formatWeight(o.weight)}`}
            </span>
          )}
          <div className="flex gap-1">
            {onKpis ? (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Indicadores vinculados a ${o.name}`}
                title="Indicadores vinculados"
                onClick={onKpis}
              >
                <Gauge aria-hidden />
              </Button>
            ) : null}
            {canEdit ? (
              <>
                <Button variant="ghost" size="icon-sm" aria-label={`Relacionar ${o.name}`} onClick={onRelate}>
                  <Link2 aria-hidden />
                </Button>
                <Button variant="ghost" size="icon-sm" aria-label={`Editar ${o.name}`} onClick={onEdit}>
                  <Pencil aria-hidden />
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}

/** Peso no próprio cartão: salva ao sair do campo (ou Enter); Esc desfaz. */
function WeightField({
  objective: o,
  organizationId,
  projectId,
}: {
  objective: Objective;
  organizationId: string;
  projectId: string;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const id = useId();
  const current = o.weight === null ? '' : String(o.weight).replace('.', ',');
  const [value, setValue] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function commit() {
    if (value.trim() === current || saving) return;
    const parsed = objectiveWeightInputSchema.safeParse({
      weight: value.trim() === '' ? null : value.trim().replace(',', '.'),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Peso inválido.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const saved = await client.request(
        `/organizations/${organizationId}/projects/${projectId}/objectives/${o.id}/weight`,
        { method: 'PUT', body: parsed.data, schema: objectiveSchema },
      );
      queryClient.setQueryData<Objective[]>(['objectives', projectId, 'board'], (prev) =>
        prev?.map((x) => (x.id === saved.id ? saved : x)),
      );
      setValue(saved.weight === null ? '' : String(saved.weight).replace('.', ','));
      await queryClient.invalidateQueries({ queryKey: ['objectives', projectId] });
    } catch (err) {
      setError(errorMessage(err));
      setValue(current);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5">
        <label htmlFor={id} className="text-xs text-muted-foreground">
          Peso
        </label>
        <input
          id={id}
          aria-label={`Peso de ${o.name} (%)`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-erro` : undefined}
          inputMode="decimal"
          maxLength={6}
          value={value}
          disabled={saving}
          placeholder="—"
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commit();
            } else if (e.key === 'Escape') {
              setValue(current);
              setError(null);
            }
          }}
          className="h-8 w-16 rounded-md border border-border-strong bg-surface-raised px-2 text-right text-sm tabular-nums focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid"
        />
        <span className="text-xs text-muted-foreground">%</span>
      </div>
      {error ? (
        <p id={`${id}-erro`} className="text-xs font-medium text-status-red-fg">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function RelateForm({
  objective,
  options,
  onSubmit,
  onCancel,
  onBusyChange,
}: {
  objective: Objective;
  options: ObjectiveOptions;
  onSubmit: (type: ObjectiveRelationTypeName, objectiveId: string) => Promise<void>;
  onCancel: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const [type, setType] = useState<ObjectiveRelationTypeName>('CAUSE_EFFECT');
  const [target, setTarget] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { type: useId(), target: useId() };
  const taken = new Set(objective.relations.filter((r) => r.type === type).map((r) => r.objective.id));
  // Horizontal só entre objetivos do mesmo nível do desdobramento.
  const candidates = options.objectives.filter(
    (o) =>
      o.id !== objective.id && !taken.has(o.id) && (type !== 'HORIZONTAL' || o.level === objective.level),
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    if (!target) return setError('Escolha o objetivo.');
    setError(null);
    setFormError(null);
    setSaving(true);
    onBusyChange(true);
    try {
      await onSubmit(type, target);
    } catch (err) {
      setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <p className="text-sm text-muted-foreground">
        <span className="font-semibold text-foreground">{objective.name}</span>
      </p>
      <FormField id={ids.type} label="Tipo de relação">
        <NativeSelect
          id={ids.type}
          value={type}
          onChange={(e) => {
            setType(e.target.value as ObjectiveRelationTypeName);
            setTarget('');
          }}
        >
          {OBJECTIVE_RELATION_TYPES.map((t) => (
            <option key={t} value={t}>
              {OBJECTIVE_RELATION_LABELS[t]}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      <FormField id={ids.target} label="Com o objetivo" error={error ?? undefined}>
        <NativeSelect
          {...fieldA11y(ids.target, error ?? undefined)}
          value={target}
          onChange={(e) => setTarget(e.target.value)}
        >
          <option value="">{candidates.length ? 'Escolha o objetivo' : 'Nenhum objetivo disponível'}</option>
          {candidates.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </NativeSelect>
      </FormField>
      <p className="text-xs text-muted-foreground">
        Causa e efeito: este objetivo contribui para o outro. Horizontal: objetivos do mesmo nível, de áreas
        que andam juntas.
      </p>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving} disabled={!candidates.length}>
          Criar relação
        </Button>
      </div>
    </form>
  );
}
